import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';

import { filter, map } from 'rxjs/operators';

import { Store } from '@ngxs/store';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

import { ProfileResponse } from '@smarthead-api/models/profile-response';

import { UiIconsList, UiIconsModule } from 'ui-icons';

import { environment } from '../../../../environments/environment';
import { PATHS_DICT, ROUTES_PREFIX } from '../../../auth/auth.constants';
import { ProfileSearchComponent } from '../../components/profile-search/profile-search.component';
import { UserLogOut } from '../../state/app.actions';
import { AppState } from '../../state/app.state';
import { ADMIN_PATHS, getProfilePath } from '../../utils/admin-paths';

const ML_PERMISSION = 'admin:ml:all';
/** On a phone the navigation is hidden and opened over the page by the menu button */
const SMALL_SCREEN_QUERY = '(max-width: 767px)';

const STAGE_LABELS: Record<typeof environment.stage, string> = {
  local: 'Local · dev2',
  test: 'Test',
  production: 'Production'
};

interface NavigationItem {
  label: string;
  path: string;
  icon: UiIconsList;
}

/**
 * Layout of every admin page: navigation on the left, the environment, profile search and the user on top.
 * It uses ng-zorro only - `ui-platform` is a single module, any of its components here would put the whole library
 * into the initial bundle.
 */
@Component({
  selector: 'sh-app-shell',
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NzDropDownModule,
    NzLayoutModule,
    NzMenuModule,
    NzToolTipModule,
    ProfileSearchComponent,
    RouterLink,
    RouterOutlet,
    UiIconsModule
  ]
})
export class AppShellComponent {
  readonly #store = inject(Store);
  readonly #router = inject(Router);

  protected readonly stage = environment.stage;
  protected readonly stageLabel = STAGE_LABELS[environment.stage];
  protected readonly collapsed = signal(false);
  protected readonly isSmallScreen = toSignal(
    inject(BreakpointObserver)
      .observe(SMALL_SCREEN_QUERY)
      .pipe(map(state => state.matches)),
    { initialValue: window.matchMedia(SMALL_SCREEN_QUERY).matches }
  );

  protected readonly user = this.#store.selectSignal(AppState.getUser);
  protected readonly navigationItems = computed<NavigationItem[]>(() => [
    { label: 'Profiles', path: ADMIN_PATHS.PROFILES, icon: 'activity' },
    { label: 'Users', path: ADMIN_PATHS.USERS, icon: 'users' },
    { label: 'Consolidations', path: ADMIN_PATHS.CONSOLIDATIONS, icon: 'consolidate' },
    { label: 'Audit log', path: ADMIN_PATHS.AUDIT_LOG, icon: 'workflow' },
    { label: 'Link shortener', path: ADMIN_PATHS.LINK_SHORTENER, icon: 'link' },
    ...(this.user()?.appMetaData?.authorization?.permissions?.includes(ML_PERMISSION)
      ? [{ label: 'ML', path: ADMIN_PATHS.ML, icon: 'knowledge' as const }]
      : [])
  ]);

  constructor() {
    if (this.isSmallScreen()) {
      this.collapsed.set(true);
    }

    // the navigation over the page is closed when a page is opened
    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (this.isSmallScreen()) {
          this.collapsed.set(true);
        }
      });
  }

  protected onProfileSelected(profile: ProfileResponse): void {
    this.#router.navigateByUrl(getProfilePath(profile.id));
  }

  protected onLogOutClick(): void {
    const returnTo = new URL(`${ROUTES_PREFIX}/${PATHS_DICT.LOGIN}`, window.location.origin);
    returnTo.searchParams.append('target', window.location.pathname);

    this.#store.dispatch(new UserLogOut({ logoutParams: { returnTo: returnTo.href } }));
  }
}
